import React, { useState, useEffect } from 'react';
import { useNotification } from '../../../context/NotificationContext';
import { adminAPI } from '../../../utils/api';

const YogaVideos = () => {

  const { showSuccess, showError } = useNotification();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    video_url: '',
    thumbnail_url: '',
    price: 0,
    duration: '',
    category: 'other',
    difficulty: 'Beginner',
    is_active: true,
    is_live_class: false,
    scheduled_start_time: '',
    scheduled_end_time: ''
  });

  useEffect(() => {
    fetchVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await adminAPI.getAllYogaVideos();
      if (response.data.success) {
        setVideos(response.data.data || []);
      }
    } catch (error) {
      showError('Failed to load yoga videos');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingVideo) {
        await adminAPI.updateYogaVideo(editingVideo._id, formData);
        showSuccess('Yoga video updated successfully');
      } else {
        await adminAPI.createYogaVideo(formData);
        showSuccess('Yoga video created successfully');
      }
      setShowForm(false);
      setEditingVideo(null);
      setFormData({
        title: '',
        description: '',
        video_url: '',
        thumbnail_url: '',
        price: 0,
        duration: '',
        category: 'other',
        difficulty: 'Beginner',
        is_active: true,
        is_live_class: false,
        scheduled_start_time: '',
        scheduled_end_time: ''
      });
      fetchVideos();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to save yoga video');
    }
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    setFormData({
      title: video.title,
      description: video.description || '',
      video_url: video.video_url,
      thumbnail_url: video.thumbnail_url || '',
      price: video.price || 0,
      duration: video.duration || '',
      category: video.category || 'other',
      difficulty: video.difficulty || 'Beginner',
      is_active: video.is_active,
      is_live_class: video.is_live_class || false,
      scheduled_start_time: video.scheduled_start_time ? new Date(video.scheduled_start_time).toISOString().slice(0, 16) : '',
      scheduled_end_time: video.scheduled_end_time ? new Date(video.scheduled_end_time).toISOString().slice(0, 16) : ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this yoga video?')) return;
    try {
      await adminAPI.deleteYogaVideo(id);
      showSuccess('Yoga video deleted successfully');
      fetchVideos();
    } catch (error) {
      showError('Failed to delete yoga video');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading yoga videos...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gold">Manage Yoga Videos</h1>
        <button
          onClick={() => {
            setShowForm(!showForm);
            setEditingVideo(null);
            setFormData({
              title: '',
              description: '',
              video_url: '',
              thumbnail_url: '',
              price: 0,
              duration: '',
              category: 'other',
              difficulty: 'Beginner',
              is_active: true,
              is_live_class: false,
              scheduled_start_time: '',
              scheduled_end_time: ''
            });
          }}
          className="bg-gold text-dark px-4 py-2 rounded-lg font-medium"
        >
          {showForm ? 'Cancel' : 'Add New Video'}
        </button>
      </div>

      {showForm && (
        <div className="bg-gray-800 rounded-xl p-6">
          <h2 className="text-xl font-semibold mb-4 text-gold">
            {editingVideo ? 'Edit Yoga Video' : 'Create New Yoga Video'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                className="w-full bg-gray-700 text-light rounded-lg px-4 py-2 border border-gray-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full bg-gray-700 text-light rounded-lg px-4 py-2 border border-gray-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Video URL (Link) *</label>
              <input
                type="url"
                name="video_url"
                value={formData.video_url}
                onChange={handleInputChange}
                required
                className="w-full bg-gray-700 text-light rounded-lg px-4 py-2 border border-gray-600"
                placeholder="https://example.com/yoga-video"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Thumbnail URL</label>
              <input
                type="url"
                name="thumbnail_url"
                value={formData.thumbnail_url}
                onChange={handleInputChange}
                className="w-full bg-gray-700 text-light rounded-lg px-4 py-2 border border-gray-600"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Price (₹)</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  min="0"
                  className="w-full bg-gray-700 text-light rounded-lg px-4 py-2 border border-gray-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Duration</label>
                <input
                  type="text"
                  name="duration"
                  value={formData.duration}
                  onChange={handleInputChange}
                  placeholder="30 mins"
                  className="w-full bg-gray-700 text-light rounded-lg px-4 py-2 border border-gray-600"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Category</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="w-full bg-gray-700 text-light rounded-lg px-4 py-2 border border-gray-600"
                >
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                  <option value="meditation">Meditation</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Difficulty</label>
                <select
                  name="difficulty"
                  value={formData.difficulty}
                  onChange={handleInputChange}
                  className="w-full bg-gray-700 text-light rounded-lg px-4 py-2 border border-gray-600"
                >
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                </select>
              </div>
            </div>

            <div className="bg-gray-700 p-4 rounded-lg border border-gray-600 my-4">
              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  name="is_live_class"
                  checked={formData.is_live_class}
                  onChange={handleInputChange}
                  className="mr-2 w-5 h-5 text-gold rounded border-gray-500 focus:ring-gold"
                />
                <label className="text-lg font-medium text-gold">This is a Live Class/Scheduled Video</label>
              </div>

              {formData.is_live_class && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Open Time (Stream Start)</label>
                    <input
                      type="datetime-local"
                      name="scheduled_start_time"
                      value={formData.scheduled_start_time}
                      onChange={handleInputChange}
                      required={formData.is_live_class}
                      className="w-full bg-gray-600 text-light rounded-lg px-4 py-2 border border-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Close Time (Stream End)</label>
                    <input
                      type="datetime-local"
                      name="scheduled_end_time"
                      value={formData.scheduled_end_time}
                      onChange={handleInputChange}
                      required={formData.is_live_class}
                      className="w-full bg-gray-600 text-light rounded-lg px-4 py-2 border border-gray-500"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
                className="mr-2"
              />
              <label className="text-sm">Active</label>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="bg-gold text-dark px-4 py-2 rounded-lg font-medium"
              >
                {editingVideo ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingVideo(null);
                }}
                className="bg-gray-700 text-light px-4 py-2 rounded-lg font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-4">
        {videos.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <p>No yoga videos found. Create your first video above.</p>
          </div>
        ) : (
          videos.map((video) => (
            <div key={video._id} className="bg-gray-800 rounded-xl p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-semibold">{video.title}</h3>
                    <span className={`px-2 py-1 rounded text-xs ${video.is_active ? 'bg-green-600' : 'bg-gray-600'}`}>
                      {video.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {video.description && (
                    <p className="text-gray-400 mb-2">{video.description}</p>
                  )}
                  <div className="grid grid-cols-2 gap-4 text-sm text-gray-400">
                    <div>
                      <p>Video URL: <span className="text-gold break-all">{video.video_url}</span></p>
                      <p>Price: <span className="text-gold">₹{video.price}</span></p>
                      {video.is_live_class && (
                        <p className="text-blue-400 mt-1">
                          Live: {new Date(video.scheduled_start_time).toLocaleString()}
                        </p>
                      )}
                    </div>
                    <div>
                      <p>Duration: {video.duration || 'N/A'}</p>
                      <p>Category: {video.category} | Difficulty: {video.difficulty}</p>
                      {video.is_live_class && (
                        <p className="text-red-400 mt-1">
                          Ends: {new Date(video.scheduled_end_time).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => handleEdit(video)}
                    className="bg-blue-600 text-white px-3 py-1 rounded text-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(video._id)}
                    className="bg-red-600 text-white px-3 py-1 rounded text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default YogaVideos;
